import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { DropdownFavoriteMasterIcon } from 'components/pages/ModeReports/DropdownFavoriteMasterIcon/DropdownFavoriteMasterIcon';
import { sortOptionsByFavorite } from 'components/pages/ModeReports/modeReportsServices';

import MultiSelect from '../../../common/data-entry/MultiSelect/MultiSelect';

import './ReportMultiSelectFilter.scss';

export const ALL_ELEMENT = { id: 'all', name: 'All' };

const customSelectedSitesValue = (items, name, dataSource) => {
  const length = items?.filter(i => i?.id !== ALL_ELEMENT.id)?.length;
  const postFix = (name && `${name} selected`) || 'selected';
  if (!dataSource?.length) {
    return `No ${name}`;
  } else if (length >= dataSource?.length) {
    return `All(${dataSource?.length}) ${postFix}`;
  } else if (length > 0 && length < 4) {
    return items.map(i => i.name).join(', ');
  }
  return `${length} ${postFix}`;
};

const ReportMultiSelectBase = ({
  dataSource,
  value,
  name,
  id,
  onChange,
  favoriteOptions,
  customOptionTemplateFunction,
  customFilterFunction,
  className = ''
}) => {
  const dataSourceIncludesFavoriteOption = useMemo(() => {
    return dataSource?.some(el => favoriteOptions?.includes(el.id));
  }, [dataSource, favoriteOptions]);

  const allAvailableFavoriteOptionsAreSelected = useMemo(() => {
    const allAvailableFavoriteOptions =
      favoriteOptions?.filter(option => dataSource.map(el => el.id).includes(option)) ?? [];
    return (
      allAvailableFavoriteOptions.length === value.length &&
      value.every(val => allAvailableFavoriteOptions.includes(val.id))
    );
  }, [dataSource, favoriteOptions, value]);

  const applyFavoriteOptions = useCallback(
    e => {
      e.stopPropagation();
      const favoriteItems = dataSource.filter(el => favoriteOptions?.includes(el.id));
      onChange(favoriteItems);
    },
    [dataSource, favoriteOptions, onChange]
  );

  const getCustomTemplateFunction = items => {
    return (
      <React.Fragment>
        {customSelectedSitesValue(items, name, dataSource)}
        <DropdownFavoriteMasterIcon
          applyFavoriteOptions={applyFavoriteOptions}
          className="master-icon-selected-values"
          disabled={!dataSourceIncludesFavoriteOption}
          allAvailableFavoriteOptionsAreSelected={allAvailableFavoriteOptionsAreSelected}
        />
      </React.Fragment>
    );
  };

  return (
    <MultiSelect
      className={`${className} report-multi-select-filter`}
      controlId={id}
      label={name}
      placeholder={
        <DropdownFavoriteMasterIcon
          applyFavoriteOptions={applyFavoriteOptions}
          className="master-icon-placeholder"
          disabled={!dataSourceIncludesFavoriteOption}
          allAvailableFavoriteOptionsAreSelected={allAvailableFavoriteOptionsAreSelected}
        />
      }
      searchable={true}
      onChange={newValues => onChange(newValues)}
      customFilterFunction={customFilterFunction}
      customSelectedValueTemplateFunction={getCustomTemplateFunction}
      customOptionTemplateFunction={customOptionTemplateFunction}
      dataSource={sortOptionsByFavorite(dataSource, favoriteOptions)}
      value={value}
      validate={false}
    />
  );
};

ReportMultiSelectBase.defaultProps = {
  dataSource: [],
  value: [],
  name: '',
  id: '',
  placeholder: null,
  onChange: () => {},
  favoriteOptions: [],
  customOptionTemplateFunction: option => option.name,
  customFilterFunction: undefined,
  searchable: true,
  validate: false
};

ReportMultiSelectBase.propTypes = {
  dataSource: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  value: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  name: PropTypes.string,
  id: PropTypes.string,
  onChange: PropTypes.func,
  favoriteOptions: PropTypes.arrayOf(PropTypes.string),
  customOptionTemplateFunction: PropTypes.func,
  customFilterFunction: PropTypes.func,
  placeholder: PropTypes.node,
  className: PropTypes.string,
  searchable: PropTypes.bool,
  validate: PropTypes.bool
};

export default ReportMultiSelectBase;
